import React, { useEffect } from 'react';
import { FileTextIcon, CalendarIcon, TagIcon, ClipboardListIcon, CheckCircleIcon, XCircleIcon, HistoryIcon, HashIcon, BookmarkIcon, XIcon, ExternalLinkIcon, InfoIcon } from 'lucide-react';
type PolicyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  policy: any; // In a real app, we would define a proper type for policy
};
export function PolicyModal({
  isOpen,
  onClose,
  policy
}: PolicyModalProps) {
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  if (!policy) return null;
  const StatusBadge = ({
    status
  }: {
    status: string;
  }) => {
    let bgColor = 'bg-gray-100 text-gray-800';
    switch (status) {
      case 'Active':
        bgColor = 'bg-green-100 text-green-800';
        break;
      case 'Draft':
        bgColor = 'bg-red-100 text-red-800';
        break;
      case 'Pending':
        bgColor = 'bg-yellow-100 text-yellow-800';
        break;
      case 'New':
        bgColor = 'bg-fuchsia-100 text-fuchsia-800';
        break;
      case 'Archived':
        bgColor = 'bg-gray-100 text-gray-800';
        break;
    }
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${bgColor}`} data-id="element-255">
        {status}
      </span>;
  };
  return <>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black bg-opacity-30 z-20 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} data-id="element-256" />
      {/* Modal */}
      <div className={`fixed top-0 right-0 bottom-0 w-full md:w-5/6 lg:w-3/5 xl:w-1/2 bg-white shadow-xl z-30 transform transition-transform duration-300 ease-in-out overflow-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-257">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b" data-id="element-258">
          <div className="flex justify-between items-center p-4" data-id="element-259">
            <h2 className="text-xl font-semibold" data-id="element-260">Policy Details</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-261">
              <XIcon size={20} className="text-gray-500" data-id="element-262" />
            </button>
          </div>
        </div>
        <div className="overflow-auto" data-id="element-263">
          {/* Header with gradient background */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-6" data-id="element-264">
            <div className="flex items-center" data-id="element-265">
              <div className="mr-5" data-id="element-266">
                <div className="bg-white p-3 rounded-lg shadow-md border border-purple-200" data-id="element-267">
                  <FileTextIcon size={32} className="text-purple-600" data-id="element-268" />
                </div>
              </div>
              <div data-id="element-269">
                <div className="flex items-center space-x-3 mb-2" data-id="element-270">
                  <h3 className="text-2xl font-bold" data-id="element-271">{policy.title}</h3>
                  <StatusBadge status={policy.status} data-id="element-272" />
                </div>
                <div className="flex items-center" data-id="element-273">
                  <TagIcon size={16} className="mr-2" data-id="element-274" />
                  <span className="text-purple-100" data-id="element-275">{policy.payer}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-8" data-id="element-276">
            {/* Policy Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-277">
              <div className="grid grid-cols-2 gap-4" data-id="element-278">
                <div className="flex items-start" data-id="element-279">
                  <CalendarIcon size={18} className="text-gray-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-280" />
                  <div data-id="element-281">
                    <div className="text-sm text-gray-500" data-id="element-282">Posted Date</div>
                    <div className="font-medium" data-id="element-283">{policy.posted}</div>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-284">
                  <CalendarIcon size={18} className="text-gray-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-285" />
                  <div data-id="element-286">
                    <div className="text-sm text-gray-500" data-id="element-287">Updated Date</div>
                    <div className="font-medium" data-id="element-288">{policy.updated}</div>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-289">
                  <CalendarIcon size={18} className="text-gray-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-290" />
                  <div data-id="element-291">
                    <div className="text-sm text-gray-500" data-id="element-292">Effective Date</div>
                    <div className="font-medium" data-id="element-293">{policy.posted}</div>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-294">
                  <CalendarIcon size={18} className="text-gray-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-295" />
                  <div data-id="element-296">
                    <div className="text-sm text-gray-500" data-id="element-297">Future Review</div>
                    <div className="font-medium" data-id="element-298">{policy.future}</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Key Information Section */}
            <div data-id="element-299">
              <div className="flex items-center mb-4" data-id="element-300">
                <InfoIcon size={18} className="text-purple-600 mr-2" data-id="element-301" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-302">
                  Key Information
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-6" data-id="element-303">
                <div className="flex items-center" data-id="element-304">
                  <TagIcon size={18} className="text-indigo-500 mr-3 flex-shrink-0" data-id="element-305" />
                  <div data-id="element-306">
                    <div className="text-sm text-gray-500" data-id="element-307">Category</div>
                    <div className="font-medium" data-id="element-308">{policy.category}</div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-309">
                  <HashIcon size={18} className="text-blue-500 mr-3 flex-shrink-0" data-id="element-310" />
                  <div data-id="element-311">
                    <div className="text-sm text-gray-500" data-id="element-312">Policy Number</div>
                    <div className="font-medium" data-id="element-313">{`IV-${Math.floor(Math.random() * 1000)}-${Math.floor(Math.random() * 1000)}`}</div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-314">
                  <BookmarkIcon size={18} className="text-emerald-500 mr-3 flex-shrink-0" data-id="element-315" />
                  <div data-id="element-316">
                    <div className="text-sm text-gray-500" data-id="element-317">Policy Type</div>
                    <div className="font-medium" data-id="element-318">{policy.policyType}</div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-319">
                  <ClipboardListIcon size={18} className="text-amber-500 mr-3 flex-shrink-0" data-id="element-320" />
                  <div data-id="element-321">
                    <div className="text-sm text-gray-500" data-id="element-322">Status</div>
                    <div className="font-medium" data-id="element-323">{policy.status}</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Divider */}
            <div className="border-t border-gray-200" data-id="element-324"></div>
            {/* Summary Section */}
            <div data-id="element-325">
              <div className="flex items-center mb-4" data-id="element-326">
                <ClipboardListIcon size={18} className="text-purple-600 mr-2" data-id="element-327" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-328">Summary</h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-329">
                <p className="text-sm text-gray-700 mb-4" data-id="element-330">
                  This policy addresses the use of {policy.title.toLowerCase()}{' '}
                  for treatment of various conditions. The policy considers the
                  use of this treatment based on clinical evidence demonstrating
                  improved health outcomes. Surgical approaches may be necessary
                  in some cases, and the policy outlines specific criteria for
                  coverage.
                </p>
                <p className="text-sm text-gray-700" data-id="element-331">
                  The policy applies to internal and external conditions and
                  highlights the lack of long-term, controlled data supporting
                  treatment efficacy and safety for some applications.
                </p>
              </div>
            </div>
            {/* Coverage Statement */}
            <div data-id="element-332">
              <div className="flex items-center mb-4" data-id="element-333">
                <CheckCircleIcon size={18} className="text-purple-600 mr-2" data-id="element-334" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-335">
                  Coverage Statement
                </h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-336">
                <p className="text-sm text-gray-700" data-id="element-337">
                  Coverage is determined based on the specific diagnosis and
                  clinical presentation. Please refer to the complete policy for
                  detailed coverage criteria.
                </p>
              </div>
            </div>
            {/* Medical Necessity Sections */}
            <div className="space-y-6" data-id="element-338">
              <div data-id="element-339">
                <div className="flex items-center mb-4" data-id="element-340">
                  <CheckCircleIcon size={18} className="text-green-600 mr-2" data-id="element-341" />
                  <h4 className="text-lg font-medium text-gray-800" data-id="element-342">
                    Medically Necessary
                  </h4>
                </div>
                <div className="border rounded-md overflow-hidden flex" data-id="element-343">
                  <div className="w-2 bg-green-500" data-id="element-344"></div>
                  <div className="p-4 flex-1" data-id="element-345">
                    <p className="text-sm text-gray-700" data-id="element-346">
                      See policy for specific criteria determining medical
                      necessity.
                    </p>
                  </div>
                </div>
              </div>
              <div data-id="element-347">
                <div className="flex items-center mb-4" data-id="element-348">
                  <XCircleIcon size={18} className="text-red-600 mr-2" data-id="element-349" />
                  <h4 className="text-lg font-medium text-gray-800" data-id="element-350">
                    Not Medically Necessary
                  </h4>
                </div>
                <div className="border rounded-md overflow-hidden flex" data-id="element-351">
                  <div className="w-2 bg-red-500" data-id="element-352"></div>
                  <div className="p-4 flex-1" data-id="element-353">
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-2" data-id="element-354">
                      <li data-id="element-355">
                        Use without meeting specific clinical criteria outlined
                        in the policy
                      </li>
                      <li data-id="element-356">
                        Experimental/investigative applications not supported by
                        clinical evidence
                      </li>
                      <li data-id="element-357">Prophylactic use in asymptomatic individuals</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            {/* Divider */}
            <div className="border-t border-gray-200" data-id="element-358"></div>
            {/* Update History */}
            <div data-id="element-359">
              <div className="flex items-center mb-4" data-id="element-360">
                <HistoryIcon size={18} className="text-purple-600 mr-2" data-id="element-361" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-362">
                  Update History
                </h4>
              </div>
              <div className="space-y-4" data-id="element-363">
                <div className="border rounded-md overflow-hidden flex" data-id="element-364">
                  <div className="w-2 bg-blue-500" data-id="element-365"></div>
                  <div className="p-4 flex-1" data-id="element-366">
                    <div className="flex justify-between items-start mb-2" data-id="element-367">
                      <h5 className="font-medium" data-id="element-368">Annual Review</h5>
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-369">
                        <CalendarIcon size={12} className="inline mr-1" data-id="element-370" />
                        {policy.updated}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-371">
                      Updated clinical criteria and references.
                    </p>
                  </div>
                </div>
                <div className="border rounded-md overflow-hidden flex" data-id="element-372">
                  <div className="w-2 bg-green-500" data-id="element-373"></div>
                  <div className="p-4 flex-1" data-id="element-374">
                    <div className="flex justify-between items-start mb-2" data-id="element-375">
                      <h5 className="font-medium" data-id="element-376">Policy Creation</h5>
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-377">
                        <CalendarIcon size={12} className="inline mr-1" data-id="element-378" />
                        {policy.posted}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-379">
                      Initial policy creation and implementation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Related Codes */}
            <div className="border-t border-gray-200 pt-6" data-id="element-380">
              <div className="flex items-center mb-4" data-id="element-381">
                <HashIcon size={18} className="text-purple-600 mr-2" data-id="element-382" />
                <h4 className="text-lg font-medium text-gray-800" data-id="element-383">
                  Related Codes
                </h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-384">
                <div className="flex flex-wrap gap-2" data-id="element-385">
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm" data-id="element-386">
                    30468
                  </span>
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm" data-id="element-387">
                    C1889
                  </span>
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm" data-id="element-388">
                    L8699
                  </span>
                </div>
                <div className="mt-3" data-id="element-389">
                  <a href="#" className="text-sm text-purple-600 hover:underline flex items-center" data-id="element-390">
                    View all related codes
                    <ExternalLinkIcon size={14} className="ml-1" data-id="element-391" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}